import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';

/**
 * Shared Add-Asset dialog used from:
 *  - Asset Master "Add asset" button (prefilled empty)
 *  - Data Entry "Create new: <name>" option (prefilled investment name)
 *
 * Required: investmentName, mainAssetClass, subAssetClass, geography.
 * Optional: institutions (multi).
 */
export default function AddAssetDialog({ open, onOpenChange, initial = {}, onCreated }) {
  const { dropdowns, addAsset, findAssetByName, sources } = useApp();

  const [form, setForm] = useState({
    investmentName: '',
    mainAssetClass: '',
    subAssetClass: '',
    geography: '',
    institutions: [],
  });
  const [instDraft, setInstDraft] = useState('');

  useEffect(() => {
    if (open) {
      setForm({
        investmentName: initial.investmentName || '',
        mainAssetClass: initial.mainAssetClass || '',
        subAssetClass: initial.subAssetClass || '',
        geography: initial.geography || '',
        institutions: initial.institutions || [],
      });
      setInstDraft('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // All known institutions across sources for datalist suggestions
  const instOptions = Array.from(
    new Set(sources.flatMap((s) => s.institutions || []))
  ).sort();

  const addInst = () => {
    const v = instDraft.trim();
    if (!v) return;
    if (form.institutions.includes(v)) { setInstDraft(''); return; }
    setForm({ ...form, institutions: [...form.institutions, v] });
    setInstDraft('');
  };
  const removeInst = (v) => setForm({ ...form, institutions: form.institutions.filter((x) => x !== v) });

  const requiredMissing =
    !form.investmentName.trim() ||
    !form.mainAssetClass ||
    !form.subAssetClass ||
    !form.geography;

  const submit = async () => {
    if (requiredMissing) {
      toast.error('Investment name, Main Asset Class, Sub-Asset Class and Geography are required.');
      return;
    }
    const dupe = findAssetByName(form.investmentName);
    if (dupe) {
      toast.error(`An asset named "${form.investmentName}" already exists (${dupe.id}).`);
      return;
    }
    try {
      const created = await addAsset(form);
      if (!created) return;
      toast.success(`Created ${created.id} · ${created.investmentName}`);
      onCreated && onCreated(created);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || 'Failed to create asset');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="add-asset-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Add asset</DialogTitle>
          <DialogDescription>
            One asset can live across multiple institutions. Fill the required fields; institutions are optional and can be added later from Data Entry too.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="overline">Investment Name <span className="text-[hsl(var(--destructive))]">*</span></Label>
            <Input
              value={form.investmentName}
              onChange={(e) => setForm({ ...form, investmentName: e.target.value })}
              placeholder="e.g. Reliance Industries"
              data-testid="asset-investment-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Main Asset Class <span className="text-[hsl(var(--destructive))]">*</span></Label>
              <Select value={form.mainAssetClass} onValueChange={(v) => setForm({ ...form, mainAssetClass: v })}>
                <SelectTrigger data-testid="asset-main-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {dropdowns.mainAssetClass.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Sub-Asset Class <span className="text-[hsl(var(--destructive))]">*</span></Label>
              <Select value={form.subAssetClass} onValueChange={(v) => setForm({ ...form, subAssetClass: v })}>
                <SelectTrigger data-testid="asset-sub-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {dropdowns.subAssetClass.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="overline">Geography <span className="text-[hsl(var(--destructive))]">*</span></Label>
            <Select value={form.geography} onValueChange={(v) => setForm({ ...form, geography: v })}>
              <SelectTrigger data-testid="asset-geo-select"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {dropdowns.geography.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="overline">Institutions <span className="text-[#AAA] normal-case text-[10px] font-normal">optional · multiple allowed</span></Label>
            <div className="flex gap-2">
              <Input
                value={instDraft}
                onChange={(e) => setInstDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInst(); } }}
                list="asset-inst-options"
                placeholder="Add institution and press Enter"
                data-testid="asset-institution-draft"
              />
              <datalist id="asset-inst-options">
                {instOptions.map((i) => <option key={i} value={i} />)}
              </datalist>
              <button type="button" className="btn-secondary" onClick={addInst} data-testid="asset-institution-add">
                <Plus size={13} /> Add
              </button>
            </div>
            {form.institutions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {form.institutions.map((inst) => (
                  <span key={inst} className="chip">
                    {inst}
                    <button onClick={() => removeInst(inst)} className="text-[#9B2C2C] hover:brightness-90" data-testid={`asset-institution-remove-${inst}`}>
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <button className="btn-secondary" onClick={() => onOpenChange(false)}>Cancel</button>
          <button
            className={`btn-primary ${requiredMissing ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={submit}
            disabled={requiredMissing}
            data-testid="confirm-add-asset"
          >
            Create asset
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
